import { observer } from 'mobx-react-lite'
import Box from '@mui/material/Box'
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup'
import ToggleButton from '@mui/material/ToggleButton'
import LightModeIcon from '@mui/icons-material/LightMode'
import DarkModeIcon from '@mui/icons-material/DarkMode'
import BrightnessAutoIcon from '@mui/icons-material/BrightnessAuto'
import { themeConfig } from '@gap/shared/theme'
import { useThemeStore } from 'theme/ThemeStore'
import { useOrgGuardStore } from '../../OrgGuardStore'

/**
 * Dev-only theme toggle. Dark mode is a development feature only.
 * This component is only rendered for developers (see ProfileMenu.tsx).
 */
export default observer(function ThemeToggleMenuItem() {
   const themeStore = useThemeStore()
   const orgGuardStore = useOrgGuardStore()

   const handleChange = (_event: React.MouseEvent<HTMLElement>, value: 'light' | 'dark' | 'os-defined' | null) => {
      if (value !== null) {
         // Extra safeguard: ensure only devs can set dark mode
         if (!orgGuardStore.isDeveloper && (value === 'dark' || value === 'os-defined')) {
            return
         }
         themeStore.setThemeVariables({
            propertyName: 'themeMode',
            newValue: value,
         })
      }
   }

   const currentMode = themeStore.themeVariables.themeMode

   return (
      <Box sx={{ p: 2 }}>
         <ToggleButtonGroup
            value={currentMode}
            exclusive
            onChange={handleChange}
            fullWidth
            size="small"
            sx={{
               '& .MuiToggleButton-root': {
                  flex: 1,
                  py: 1,
                  px: 1.5,
                  textTransform: 'none',
                  fontSize: '0.875rem',
                  fontWeight: 400,
                  borderColor: themeConfig.primary[200],
                  color: themeConfig.primary[700],
                  '&.Mui-selected': {
                     backgroundColor: themeConfig.primary[500],
                     color: 'white',
                     borderColor: themeConfig.primary[500],
                     '&:hover': {
                        backgroundColor: themeConfig.primary[600],
                     },
                  },
                  '&:hover': {
                     backgroundColor: themeConfig.primary[50],
                  },
               },
            }}
         >
            <ToggleButton value="light">
               <LightModeIcon fontSize="small" sx={{ mr: 0.75 }} />
               Light
            </ToggleButton>
            <ToggleButton value="dark">
               <DarkModeIcon fontSize="small" sx={{ mr: 0.75 }} />
               Dark
            </ToggleButton>
            <ToggleButton value="os-defined">
               <BrightnessAutoIcon fontSize="small" sx={{ mr: 0.75 }} />
               OS
            </ToggleButton>
         </ToggleButtonGroup>
      </Box>
   )
})
